/**
 * autoGreet — Data-aware greeting + pill options per view.
 * Shared by useSkerpaAutoGreet (chat auto-greet on view change) and ViewNudge.
 * buildNudgeCopy returns the short label/prompt for ViewNudge;
 * buildAutoGreet wraps it with an intro + pill chips for injected chat messages.
 */

import type { CanvasSectionState } from '@/stores/skerpaContextStore'

export interface GreetInput {
  activeView: string
  pageContext: string
  canvasSectionState: CanvasSectionState | null
  domainScores: { market: number; business: number; solution: number }
  currentPhaseName: string | null
  currentPhaseDomain: string | null
}

export interface NudgeCopy { label: string; prompt: string }

const CANVAS_NAME: Record<string, string> = {
  'canvas-business': 'Business Model Canvas',
  'canvas-market': 'Market Canvas',
  'canvas-solution': 'Solution Canvas',
}

const VIEW_DOMAIN: Record<string, 'market' | 'business' | 'solution'> = {
  'persona-configurator': 'market', 'persona-workshop': 'market',
  'canvas-market': 'market', 'value-design': 'market',
  'market-segmentation': 'market',
  'competitor-map': 'business', 'canvas-business': 'business',
  'canvas-solution': 'solution', 'solution-configurator': 'solution',
  'solution-workspace': 'solution', 'requirements': 'solution',
}

function parseCompetitors(ctx: string): { tracked: number; suggested: number } | null {
  const m = ctx.match(/Competitor Map: (\d+) tracked, (\d+) suggested/)
  if (m) return { tracked: parseInt(m[1], 10), suggested: parseInt(m[2], 10) }
  if (ctx.includes('No competitors tracked')) return { tracked: 0, suggested: 0 }
  return null
}

function parseRequirements(ctx: string): { total: number; themes: number } | null {
  const m = ctx.match(/(\d+) requirements across (\d+) themes/)
  if (m) return { total: parseInt(m[1], 10), themes: parseInt(m[2], 10) }
  if (ctx.includes('No requirements created yet')) return { total: 0, themes: 0 }
  return null
}

function parsePersonas(ctx: string): number | null {
  const m = ctx.match(/Personas: (\d+) total/)
  if (m) return parseInt(m[1], 10)
  if (ctx.includes('No personas created yet')) return 0
  return null
}

function parseSegments(ctx: string): { count: number; hasBeachhead: boolean } | null {
  const m = ctx.match(/Market Segments: (\d+) defined/)
  if (m) return { count: parseInt(m[1], 10), hasBeachhead: ctx.includes('Beachhead:') && !ctx.includes('Beachhead: not selected') }
  if (ctx.includes('no segments defined yet')) return { count: 0, hasBeachhead: false }
  return null
}

// Only redirect to weaker domain when current is >=25pts stronger AND weak <40%
function weakerDomainPill(input: GreetInput): string | null {
  const viewDomain = VIEW_DOMAIN[input.activeView]
  if (!viewDomain) return null
  const entries = Object.entries(input.domainScores) as Array<['market' | 'business' | 'solution', number]>
  const [weakDomain, weakScore] = entries.sort((a, b) => a[1] - b[1])[0]
  if (weakDomain === viewDomain) return null
  const currentScore = input.domainScores[viewDomain]
  if (currentScore - weakScore < 25 || weakScore >= 40) return null
  return `>> Actually, should I focus on ${weakDomain} first?`
}

export function buildNudgeCopy(input: GreetInput): NudgeCopy | null {
  const { activeView, pageContext, canvasSectionState } = input

  if (CANVAS_NAME[activeView]) {
    const name = CANVAS_NAME[activeView]
    const empty = canvasSectionState?.emptySections || []
    if (empty.length > 0) return {
      label: `Draft ${empty.length} of ${canvasSectionState!.totalSections} empty sections`,
      prompt: `I'm on the ${name}. ${empty.length} of ${canvasSectionState!.totalSections} sections are empty: ${empty.join(', ')}. Based on my personas, competitors, and value proposition, what should go in each?`,
    }
    return { label: `Audit my ${name}`, prompt: `Look at my ${name}. Is it coherent with my personas and competitors? What's weakest or inconsistent?` }
  }

  if (activeView === 'competitor-map') {
    const c = parseCompetitors(pageContext)
    if (c && c.tracked === 0) return { label: 'Suggest competitors from my data', prompt: "I haven't added any competitors yet. Based on my personas and market, who are my main direct, adjacent, and potential competitors? Suggest them with categories." }
    if (c && c.tracked > 0) return { label: `Analyze my ${c.tracked} competitors`, prompt: `Look at my ${c.tracked} competitors. What gaps, overlaps, or missing categories do you see? Who else should I track?` }
    return { label: 'Help me with competitors', prompt: 'Help me map my competitive landscape from my project data.' }
  }

  if (activeView === 'requirements') {
    const r = parseRequirements(pageContext)
    if (r && r.total === 0) return { label: 'Generate a PRD from my data', prompt: 'I have no requirements yet. Generate a starter PRD (themes, epics, stories) grouped into sections (overview / functional / non-functional) from my personas, pains, and value proposition.' }
    if (r && r.total > 0) return { label: `Audit ${r.total} requirements for gaps`, prompt: `I have ${r.total} requirements across ${r.themes} themes. Audit them — are they traceable to validated pains? What gaps, duplicates, or missing themes do you see?` }
    return { label: 'Help me with requirements', prompt: 'Help me structure and prioritize my product requirements.' }
  }

  if (activeView === 'persona-configurator' || activeView === 'persona-workshop') {
    const n = parsePersonas(pageContext)
    if (n === 0) return { label: 'Help me define my first persona', prompt: 'Help me create my first persona based on my project context. Who should I start with?' }
    if (n && n > 0) return { label: `Review my ${n} persona${n === 1 ? '' : 's'}`, prompt: `Look at my ${n} personas. Are they specific enough? What jobs, pains, or gains am I missing?` }
    return { label: 'Review my personas', prompt: 'Look at my personas — what gaps do you see?' }
  }

  if (activeView === 'market-segmentation') {
    const s = parseSegments(pageContext)
    if (s && s.count === 0) return { label: 'Generate segments from my personas', prompt: "I haven't defined any market segments yet. Based on my personas and market research, generate a starter set of segments with scoring criteria and sizing estimates." }
    if (s && s.count > 0 && !s.hasBeachhead) return { label: `Pick the beachhead from my ${s.count} segments`, prompt: `Look at my ${s.count} market segments. Based on weighted scores and strategic fit, which should be my beachhead? Explain the tradeoffs.` }
    if (s && s.count > 0) return { label: `Evaluate my ${s.count} segments`, prompt: `Review my ${s.count} market segments and beachhead choice. What scoring criteria, sizing data, or segments am I missing? Any weak assumptions?` }
    return { label: 'Help me with segments', prompt: 'Help me identify and evaluate market segments from my project data.' }
  }

  return STATIC_NUDGES[activeView] || null
}

const STATIC_NUDGES: Record<string, NudgeCopy> = {
  'value-design': { label: 'Review my value proposition', prompt: 'Look at my Value Proposition Canvas. Do pain relievers and gain creators map to the highest-severity pains?' },
  'solution-workspace': { label: 'Review my solution', prompt: 'Look at my solution workspace. Are vision, features, and requirements traceable to validated customer needs?' },
  'solution-configurator': { label: 'Review my solution', prompt: 'Look at my solution workspace. Are vision, features, and requirements traceable to validated customer needs?' },
  'workspace': { label: 'What should I focus on next?', prompt: 'Based on my journey progress, domain scores, and entity counts, what should I work on right now?' },
  'sources': { label: 'What should I upload?', prompt: 'Based on my project stage and evidence gaps, what documents or transcripts should I upload?' },
  'analysis': { label: 'Where are my evidence gaps?', prompt: "I'm on the validation analysis. Where are the biggest gaps and what should I do to close them?" },
  'discovery': { label: 'Guide my discovery', prompt: 'Based on my discovery progress, what should I focus on next?' },
  'interviewer': { label: 'Analyze this interview', prompt: 'Analyze the interview on screen — what key insights, pains, jobs, and quotes should I capture?' },
  'ai-interview': { label: 'Help me plan an interview', prompt: 'Based on my personas and assumptions to validate, what questions should I ask in an AI interview?' },
  'dual-space': { label: 'Link problems to solutions', prompt: "I'm in the Problem-Solution Map. Help me connect validated problems to features with clear traceability." },
}

const ALT_PILLS: Record<string, string> = {
  'canvas-business': ">> Show me what I'm missing",
  'canvas-market': ">> Show me what I'm missing",
  'canvas-solution': ">> Show me what I'm missing",
  'competitor-map': '>> Categorize my competitors',
  'requirements': '>> Prioritize by validated pain',
  'persona-configurator': '>> Draft jobs/pains/gains for me',
  'persona-workshop': '>> Draft jobs/pains/gains for me',
  'value-design': '>> Suggest pain relievers from my features',
  'solution-workspace': '>> Find orphan features (no linked pain)',
  'solution-configurator': '>> Find orphan features (no linked pain)',
  'market-segmentation': '>> Score segments against my strategy',
}

function introSentence(input: GreetInput): string {
  const { activeView, canvasSectionState, pageContext } = input
  const canvasName = CANVAS_NAME[activeView]
  if (canvasName && canvasSectionState) {
    const { emptySections, totalSections } = canvasSectionState
    const filled = totalSections - emptySections.length
    return emptySections.length > 0
      ? `You're on the ${canvasName} — ${filled}/${totalSections} sections filled.`
      : `You're on the ${canvasName} — all sections have content.`
  }
  if (activeView === 'competitor-map') {
    const c = parseCompetitors(pageContext)
    return c && c.tracked > 0 ? `You're on the Competitor Map with ${c.tracked} tracked.` : "You're on the Competitor Map — nothing tracked yet."
  }
  if (activeView === 'requirements') {
    const r = parseRequirements(pageContext)
    return r && r.total > 0 ? `You're on Requirements with ${r.total} items across ${r.themes} themes.` : "You're on Requirements — no PRD yet."
  }
  if (activeView === 'persona-configurator' || activeView === 'persona-workshop') {
    const n = parsePersonas(pageContext)
    const label = activeView === 'persona-workshop' ? 'Persona Workshop' : 'Persona Configurator'
    return n && n > 0 ? `You're in the ${label} with ${n} persona${n === 1 ? '' : 's'}.` : `You're in the ${label} — no personas yet.`
  }
  if (activeView === 'market-segmentation') {
    const s = parseSegments(pageContext)
    if (!s) return "You're on Market Segments."
    if (s.count === 0) return "You're on Market Segments — none defined yet."
    return `You're on Market Segments — ${s.count} defined${s.hasBeachhead ? ', beachhead picked' : ', no beachhead yet'}.`
  }
  const pretty = activeView.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  return `You're on ${pretty}.`
}

export function buildAutoGreet(input: GreetInput): string | null {
  const nudge = buildNudgeCopy(input)
  if (!nudge) return null
  const pills = [`>> ${nudge.label}`]
  const alt = ALT_PILLS[input.activeView]
  if (alt) pills.push(alt)
  const journey = weakerDomainPill(input)
  if (journey) pills.push(journey)
  return `${introSentence(input)}\n\n${pills.join('\n')}`
}
